import { t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import {
  ArrowRightIcon,
  CheckCircleIcon,
  CircleNotchIcon,
  CopyIcon,
  WarningCircleIcon,
} from "@phosphor-icons/react";
import type { LinkedInEndorsementInput, LinkedInEndorsementMode } from "@reactive-resume/dto";
import { linkedinEndorsementSchema } from "@reactive-resume/dto";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router";

import { SEO } from "@/client/components/seo";

import { RateLimitWaitlistForm } from "../_components/rate-limit-waitlist-form";
import { ToolShareButtons } from "../_components/tool-share-buttons";
import { buildPublicToolUrl, getApiErrorMessage, recordToolCtaClick } from "../_utils";

type ParsedEvent = {
  event: string;
  data: string;
};

type DraftTone = "formal" | "warm" | "concise";

type DraftCard = {
  tone: DraftTone;
  label: string;
  text: string;
};

type FormState = LinkedInEndorsementInput;
type FormErrors = Partial<Record<keyof FormState, string>>;

const toolPath = "/tools/linkedin-endorsement";
const minRecommendedLength = 50;
const delimiterPattern = /---(FORMAL|WARM|CONCISE)---/g;

const toneColors: Record<DraftTone, { border: string; label: string; bg: string }> = {
  formal: {
    border: "border-sky-400/30",
    label: cn("text-sky-700 dark:text-sky-300"),
    bg: "bg-sky-400/5",
  },
  warm: {
    border: "border-orange-400/30",
    label: cn("text-orange-700 dark:text-orange-300"),
    bg: "bg-orange-400/5",
  },
  concise: {
    border: "border-emerald-400/30",
    label: cn("text-emerald-700 dark:text-emerald-300"),
    bg: "bg-emerald-400/5",
  },
};

const parseSseEvent = (rawEvent: string): ParsedEvent => {
  const lines = rawEvent.split(/\r?\n/);
  let event = "message";
  const data: string[] = [];

  for (const line of lines) {
    if (line.startsWith("event:")) {
      event = line.slice(6).trim();
      continue;
    }

    if (line.startsWith("data:")) {
      const value = line.slice(5);
      data.push(value.startsWith(" ") ? value.slice(1) : value);
    }
  }

  return { event, data: data.join("\n") };
};

const getModeCopy = (): Record<
  LinkedInEndorsementMode,
  { title: string; description: string }
> => ({
  recommendation: {
    title: t`Recommendation`,
    description: t`Detailed & professional`,
  },
  endorsement: {
    title: t`Endorsement`,
    description: t`Short & punchy`,
  },
});

const toneLabel = (tone: DraftTone): string => {
  if (tone === "formal") return t`Formal`;
  if (tone === "warm") return t`Warm`;
  return t`Concise`;
};

const parseDrafts = (value: string): DraftCard[] => {
  const matches = [...value.matchAll(delimiterPattern)];

  if (matches.length === 0) {
    return [];
  }

  return matches
    .map((match, index) => {
      const start = match.index;
      const contentStart = start + match[0].length;
      const end = matches[index + 1]?.index ?? value.length;
      const marker = match[1] as Uppercase<DraftTone>;
      const text = value.slice(contentStart, end).trim();

      if (!text) {
        return null;
      }

      const tone = marker.toLowerCase() as DraftTone;
      const label = toneLabel(tone);

      return { tone, label, text };
    })
    .filter((draft): draft is DraftCard => draft !== null);
};

/* eslint-disable lingui/no-unlocalized-strings -- schema.org JSON-LD vocabulary for crawlers, not UI */
const buildStructuredData = (toolUrl: string) => ({
  "@context": "https://schema.org",
  "@type": "WebApplication",
  name: "Apply Edge LinkedIn Endorsement Creator",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  url: toolUrl,
  description:
    "Generate LinkedIn recommendations and endorsements in formal, warm, and concise tones with live AI streaming.",
  offers: {
    "@type": "Offer",
    price: "0",
    priceCurrency: "USD",
  },
});
/* eslint-enable lingui/no-unlocalized-strings */

export const LinkedInEndorsementPage = () => {
  const { i18n } = useLingui();
  const [form, setForm] = useState<FormState>({
    profileContext: "",
    relationship: "",
    mode: "recommendation",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [streamText, setStreamText] = useState("");
  const [drafts, setDrafts] = useState<DraftCard[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState("");
  const [rateLimitMessage, setRateLimitMessage] = useState("");
  const [copiedTone, setCopiedTone] = useState<DraftTone | null>(null);

  const toolUrl = buildPublicToolUrl(toolPath);
  const shareMessage = t`I just created a LinkedIn recommendation with AI. Try it free: ${toolUrl}`;
  const warnings = {
    profileContext:
      form.profileContext.trim().length >= 10 &&
      form.profileContext.trim().length < minRecommendedLength
        ? t`More detail will produce stronger drafts. Aim for at least 50 characters.`
        : "",
    relationship:
      form.relationship.trim().length >= 10 &&
      form.relationship.trim().length < minRecommendedLength
        ? t`More relationship context helps the AI sound credible. Aim for at least 50 characters.`
        : "",
  };

  const resetResults = () => {
    setError("");
    setRateLimitMessage("");
    setStreamText("");
    setDrafts([]);
    setIsComplete(false);
    setCopiedTone(null);
  };

  const onFieldChange = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: undefined }));
  };

  const validateForm = () => {
    const parsed = linkedinEndorsementSchema.safeParse(form);

    if (parsed.success) {
      setErrors({});
      return true;
    }

    const nextErrors: FormErrors = {};

    for (const issue of parsed.error.issues) {
      const field = issue.path[0];
      if (typeof field === "string" && !(field in nextErrors)) {
        nextErrors[field as keyof FormState] = issue.message;
      }
    }

    setErrors(nextErrors);
    return false;
  };

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    resetResults();

    if (!validateForm()) {
      return;
    }

    setIsStreaming(true);

    try {
      const response = await fetch("/api/tools/linkedin-endorsement", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        const message = getApiErrorMessage(payload, t`Something went wrong. Please try again.`);

        if (response.status === 429) {
          setRateLimitMessage(message);
        } else {
          setError(message);
        }

        return;
      }

      if (!response.body) {
        setError(t`The stream ended before any drafts were received.`);
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let aggregated = "";
      let streamDone = false;

      while (!streamDone) {
        const { value, done } = await reader.read();
        buffer += decoder.decode(value ?? new Uint8Array(), { stream: !done });

        const chunks = buffer.split("\n\n");
        buffer = chunks.pop() ?? "";

        for (const rawChunk of chunks) {
          if (!rawChunk.trim()) {
            continue;
          }

          const parsed = parseSseEvent(rawChunk);

          if (parsed.event === "done") {
            streamDone = true;
            break;
          }

          if (parsed.event === "error") {
            let payload: unknown = {};

            try {
              payload = JSON.parse(parsed.data);
            } catch {
              payload = {};
            }

            throw new Error(
              getApiErrorMessage(payload, t`AI service temporarily unavailable. Please try again.`),
            );
          }

          if (parsed.data) {
            aggregated += parsed.data;
            setStreamText(aggregated);
          }
        }

        if (done) {
          streamDone = true;
        }
      }

      const parsedDrafts = parseDrafts(aggregated);
      if (parsedDrafts.length === 0) {
        setError(t`The AI response was incomplete. Please try again.`);
        return;
      }

      setDrafts(parsedDrafts);
      setIsComplete(true);
    } catch (streamError) {
      setError(
        streamError instanceof Error
          ? streamError.message
          : t`Something went wrong while generating drafts.`,
      );
    } finally {
      setIsStreaming(false);
    }
  };

  const copyDraft = async (draft: DraftCard) => {
    try {
      await navigator.clipboard.writeText(draft.text);
      setCopiedTone(draft.tone);
      window.setTimeout(() => {
        setCopiedTone((current) => (current === draft.tone ? null : current));
      }, 1500);
    } catch {
      setError(t`Copy failed. Please select the text manually.`);
    }
  };

  const streamingPreview = streamText.replace(delimiterPattern, "\n\n").trim();
  const structuredData = buildStructuredData(toolUrl);

  return (
    <main className="min-h-screen bg-background px-4 pb-24 pt-32 delay-100 duration-500 animate-in fade-in slide-in-from-bottom-4 fill-mode-both sm:px-6 sm:pt-36">
      <SEO
        lang={i18n.locale}
        title={t`LinkedIn Endorsement Creator | Apply Edge`}
        description={t`Generate LinkedIn recommendations and endorsements in multiple tones with live AI streaming.`}
        canonical={toolPath}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <div className="mx-auto flex max-w-6xl flex-col gap-8">
        <div className="max-w-3xl space-y-4">
          <Badge className="w-fit border border-orange-400/30 bg-orange-400/10 text-xs font-medium text-orange-700 dark:text-orange-300">
            {t`Free Tool`}
          </Badge>
          <h1 className="text-4xl font-semibold tracking-tight sm:text-5xl">
            {t`Write the LinkedIn praise without sounding like a template.`}
          </h1>
          <p className="max-w-2xl text-base leading-7 text-muted-foreground sm:text-lg">
            {t`Describe the person, explain how you know them, and get three polished drafts in distinct tones. Use recommendation mode for a fuller paragraph or endorsement mode when you need something shorter.`}
          </p>
        </div>

        {rateLimitMessage ? (
          <Card className="border-amber-400/30 shadow-sm transition-all duration-150 ease-out hover:-translate-y-[1px] hover:shadow-md">
            <CardHeader className="space-y-2">
              <CardTitle className="flex items-center gap-2 text-2xl text-foreground">
                <WarningCircleIcon className="text-warning-strong" size={22} />
                {t`Daily limit reached`}
              </CardTitle>
              <CardDescription className="text-sm text-muted-foreground">
                {rateLimitMessage}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {t`Leave your email and we'll tell you when unlimited drafting is available.`}
              </p>
              <RateLimitWaitlistForm tool="LINKEDIN_ENDORSEMENT" />
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6 lg:grid-cols-[1.05fr_0.95fr]">
            <Card className="border-border shadow-sm transition-all duration-150 ease-out hover:-translate-y-[1px] hover:shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl text-foreground">{t`Draft inputs`}</CardTitle>
                <CardDescription className="text-sm text-muted-foreground">
                  {t`Minimum 10 characters per field. Stronger inputs produce stronger drafts.`}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form className="space-y-5" onSubmit={onSubmit}>
                  <div className="space-y-3">
                    <div className="text-sm font-medium text-foreground">{t`Choose the output style`}</div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      {(["recommendation", "endorsement"] as LinkedInEndorsementMode[]).map(
                        (mode) => {
                          const selected = form.mode === mode;

                          return (
                            <button
                              key={mode}
                              type="button"
                              className={cn(
                                "rounded-2xl border p-4 text-left transition",
                                selected
                                  ? "border-orange-300/60 bg-orange-400/10 shadow-[0_0_0_1px_rgba(251,146,60,0.2)]"
                                  : "border-border bg-muted/40 hover:border-sky-300/40 hover:bg-sky-400/5",
                              )}
                              onClick={() => {
                                onFieldChange("mode", mode);
                              }}
                            >
                              <div className="flex items-center justify-between gap-3">
                                <span className="text-base font-semibold text-foreground">
                                  {getModeCopy()[mode].title}
                                </span>
                                {selected ? (
                                  <CheckCircleIcon
                                    className="text-orange-700 dark:text-orange-200"
                                    size={18}
                                  />
                                ) : null}
                              </div>
                              <p className="mt-2 text-sm text-muted-foreground">
                                {getModeCopy()[mode].description}
                              </p>
                            </button>
                          );
                        },
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label
                      className="text-sm font-medium text-foreground"
                      htmlFor="profile-context-input"
                    >
                      {t`Profile context`}
                    </label>
                    <textarea
                      id="profile-context-input"
                      rows={8}
                      value={form.profileContext}
                      maxLength={3000}
                      className={cn(
                        "min-h-[190px] w-full rounded-2xl border bg-muted px-4 py-3 text-sm text-foreground outline-none transition placeholder:text-muted-foreground focus:border-orange-300/60 focus:ring-2 focus:ring-orange-300/20",
                        errors.profileContext ? "border-rose-400/60" : "border-border",
                      )}
                      placeholder={t`Summarize the person's role, strengths, projects, and what makes them credible.`}
                      onChange={(event) => {
                        onFieldChange("profileContext", event.target.value);
                      }}
                    />
                    <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
                      <span>
                        {(() => {
                          const len = form.profileContext.length;
                          return t`${len}/3000 characters`;
                        })()}
                      </span>
                      {warnings.profileContext ? (
                        <span className="text-warning-strong">{warnings.profileContext}</span>
                      ) : null}
                    </div>
                    {errors.profileContext ? (
                      <p className="text-sm text-error">{errors.profileContext}</p>
                    ) : null}
                  </div>

                  <div className="space-y-2">
                    <label
                      className="text-sm font-medium text-foreground"
                      htmlFor="relationship-input"
                    >
                      {t`Your relationship`}
                    </label>
                    <textarea
                      id="relationship-input"
                      rows={7}
                      value={form.relationship}
                      maxLength={3000}
                      className={cn(
                        "min-h-[170px] w-full rounded-2xl border bg-muted px-4 py-3 text-sm text-foreground outline-none transition placeholder:text-muted-foreground focus:border-orange-300/60 focus:ring-2 focus:ring-orange-300/20",
                        errors.relationship ? "border-rose-400/60" : "border-border",
                      )}
                      placeholder={t`Explain how you worked together, for how long, and the qualities you saw firsthand.`}
                      onChange={(event) => {
                        onFieldChange("relationship", event.target.value);
                      }}
                    />
                    <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
                      <span>
                        {(() => {
                          const len = form.relationship.length;
                          return t`${len}/3000 characters`;
                        })()}
                      </span>
                      {warnings.relationship ? (
                        <span className="text-warning-strong">{warnings.relationship}</span>
                      ) : null}
                    </div>
                    {errors.relationship ? (
                      <p className="text-sm text-error">{errors.relationship}</p>
                    ) : null}
                  </div>

                  {error ? (
                    <div className="rounded-2xl border border-error/30 bg-error/10 p-4 text-sm text-error">
                      <p>{error}</p>
                      <Button
                        type="button"
                        variant="outline"
                        className="mt-3 border-error/40 bg-transparent text-error"
                        onClick={() => {
                          setError("");
                        }}
                      >
                        {t`Try again`}
                      </Button>
                    </div>
                  ) : null}

                  <Button type="submit" className="h-11 w-full" disabled={isStreaming}>
                    {isStreaming ? (
                      <>
                        <CircleNotchIcon className="mr-2 animate-spin" size={18} />
                        {t`Writing your drafts...`}
                      </>
                    ) : (
                      t`Generate drafts`
                    )}
                  </Button>

                  <div className="rounded-2xl border border-sky-400/20 bg-sky-400/10 p-4 text-sm text-sky-700 dark:text-sky-300">
                    {t`Daily limit: 2 generations per IP. Drafts stream live and separate into cards when complete.`}
                  </div>
                </form>
              </CardContent>
            </Card>

            <Card className="border-border shadow-sm transition-all duration-150 ease-out hover:-translate-y-[1px] hover:shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl text-foreground">
                  {isComplete ? t`Your LinkedIn drafts` : t`Live generation feed`}
                </CardTitle>
                <CardDescription className="text-sm text-muted-foreground">
                  {t`You'll get formal, warm, and concise versions built from the same context.`}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {isStreaming && streamText ? (
                  <div className="rounded-3xl border border-border bg-muted p-5">
                    <div className="mb-3 text-xs font-medium text-orange-700 dark:text-orange-200">
                      {t`Streaming output`}
                    </div>
                    <pre className="whitespace-pre-wrap break-words font-sans text-sm leading-7 text-foreground">
                      {streamingPreview}
                      <span className="animate-pulse text-orange-700 dark:text-orange-200">|</span>
                    </pre>
                  </div>
                ) : null}

                {!isStreaming && !isComplete ? (
                  <div className="rounded-3xl border border-border bg-muted/40 p-6 text-sm leading-7 text-muted-foreground">
                    {t`Your streamed draft text will appear here first, then we'll split it into tone-specific cards you can copy one by one.`}
                  </div>
                ) : null}

                {isComplete && drafts.length > 0 ? (
                  <>
                    <div className="grid gap-4">
                      {drafts.map((draft) => (
                        <div
                          key={draft.tone}
                          className={cn(
                            "rounded-3xl border p-5",
                            toneColors[draft.tone].border,
                            toneColors[draft.tone].bg,
                          )}
                        >
                          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                            <div
                              className={cn("text-xs font-medium", toneColors[draft.tone].label)}
                            >
                              {draft.label}
                            </div>
                            <Button
                              type="button"
                              variant="outline"
                              className="border-border bg-transparent"
                              onClick={() => {
                                void copyDraft(draft);
                              }}
                            >
                              <CopyIcon className="mr-2" size={16} />
                              {copiedTone === draft.tone ? t`Copied` : t`Copy draft`}
                            </Button>
                          </div>
                          <p className="whitespace-pre-wrap break-words text-sm leading-7 text-foreground">
                            {draft.text}
                          </p>
                        </div>
                      ))}
                    </div>

                    <ToolShareButtons
                      message={shareMessage}
                      title={t`Apply Edge LinkedIn Endorsement Creator`}
                      url={toolUrl}
                    />

                    <div className="rounded-3xl border border-orange-400/20 bg-orange-400/10 p-5">
                      <div className="space-y-3">
                        <div className="text-xs font-medium text-orange-800 dark:text-orange-100">{t`Next step`}</div>
                        <p className="text-sm leading-7 text-orange-900 dark:text-orange-50">
                          {t`Build a resume that matches your endorsements and turns social proof into stronger applications.`}
                        </p>
                        <Button asChild className="w-full sm:w-auto">
                          <Link
                            to="/auth/register"
                            onClick={() => {
                              recordToolCtaClick("LINKEDIN_ENDORSEMENT", "results");
                            }}
                          >
                            {t`Build a resume that matches your endorsements`}
                            <ArrowRightIcon className="ml-2" size={18} />
                          </Link>
                        </Button>
                      </div>
                    </div>
                  </>
                ) : null}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </main>
  );
};
